import React, { useCallback, useEffect, useState } from 'react';
import NucleusPage from '../../components/nucleus/NucleusPage';
import { Facts, Note, Tags } from '../../components/nucleus/Inspector';
import DataTable from '../../components/nucleus/DataTable';
import { useToast } from '../../components/common';
import { useGroup } from '../../contexts/GroupContext';
import { usePlace } from '../../nucleus/place';
import api from '../../services/api';
import { shareOf, accessOf, sharesWords } from '../groups/groupsData';
import { shareContractPath, accessContractPath } from '../groups/ContractRow';
import { realmShort } from '../nucleus/groupRealms';
import { FormColumn, FormBand } from './selfForm';
import { backTo } from './selfData';
import { dateOf, plural } from '../../nucleus/format';

// what members share, one line each: the item, its kind, who shared it
const SHARED_COLUMNS = [
    { id: 'item', label: 'Item', lock: true, min: 180, grow: 2, value: (n) => n.title || 'Untitled', cell: (n) => n.title || 'Untitled' },
    { id: 'kind', label: 'Kind', min: 100, grow: 1, value: (n) => n.schema_name || null, cell: (n) => n.schema_name || '—' },
    { id: 'by', label: 'Shared by', min: 100, grow: 1, value: (n) => n.shared_by?.username || null, cell: (n) => n.shared_by?.username || '—' },
];

// YOUR SHARE — your two contracts with the group, in reading order: what you
// share with it (your member → group contract: which of your realms its members
// reach, changed on the consent page) and what you can do in it (the group's
// contract with you: your role and realm). Then what every member shares with it.
// A paused share grants nothing until you resume it; it reads "Work (paused)", as
// the groups list does.
export default function SharePage() {
    const { section, page, groupId, spaceName, navigate, to } = usePlace();
    const toast = useToast();
    const { switchToPersonal } = useGroup();
    const [mine, setMine] = useState(undefined); // undefined loading
    const [realms, setRealms] = useState([]);
    const [shared, setShared] = useState(null);

    const load = useCallback(async () => {
        const [g, s] = await Promise.all([
            api.getGroup(groupId).catch(() => null),
            // when it began, and its term (a 404 is "no share row", not a failure)
            api.getGroupShare(groupId).catch(() => null),
        ]);
        // not a member, or no such group: nothing is drawn (GroupGuard takes you out)
        if (!g) return;
        setMine({
            role: g.my_role || null,
            share: shareOf(g.my_share),
            access: accessOf(g.my_access),
            since: s?.created_at || null,
            expires: s?.expires_at || null,
        });
    }, [groupId]);
    useEffect(() => {
        setMine(undefined);
        load();
        api.getPrivacyRealms().then((r) => setRealms(Array.isArray(r) ? r : r?.realms || r?.profiles || [])).catch(() => {});
    }, [load]);
    const shareKey = mine?.share ? `${mine.share.epId}:${mine.share.realmIds.join(',')}` : '';
    useEffect(() => {
        let live = true;
        api.getGroupSharedNodes(groupId, { limit: 50 })
            .then((r) => { if (live) setShared({ nodes: r?.nodes || [], total: r?.total || 0 }); })
            .catch(() => { if (live) setShared({ nodes: [], total: 0, error: true }); });
        return () => { live = false; };
    }, [groupId, shareKey]);

    const share = mine?.share || null;
    const sharing = !!share?.realms.length;
    const paused = sharing && share.active === false;
    const through = (share?.realmIds || []).map((id) => realms.find((r) => String(r.id) === id)).filter(Boolean);
    const access = mine?.access || null;
    const admin = mine?.role === 'admin';
    const authorize = () => navigate(`/grant?to_group_id=${groupId}&redirect_uri=${backTo(`/group/${groupId}/self/share`)}`);
    // yours is a contract you own: it opens among your own, in your own space
    const openShare = () => { switchToPersonal?.(); navigate(shareContractPath(share.epId)); };
    const link = (label, run) => <button type="button" className="nx-link" onClick={run}>{label}</button>;

    return (
        <NucleusPage section={section.label} title={page.label}>
            {mine !== undefined && (
                <FormColumn>
                    <FormBand
                        title={`What you share with ${spaceName}`}
                        aside={share ? link('Contract', openShare) : null}
                        lead={paused
                            ? `Paused: ${spaceName}’s members reach nothing of yours until you resume. The contract and its realms stay as they are.`
                            : `Which of your own realms ${spaceName}’s members can reach. Sharing nothing empties this contract; it is not deleted.`}
                        acts={[
                            { id: 'share', label: sharing ? 'Change what you share' : 'Share with it', primary: !paused, run: authorize },
                            paused && {
                                id: 'resume', label: 'Resume', primary: true,
                                run: async () => { await api.resumeExposureProfile(share.epId); toast.success(`You share with ${spaceName} again`); await load(); },
                            },
                            sharing && {
                                id: 'nothing', label: 'Share nothing', danger: true,
                                confirm: {
                                    title: `Share nothing with ${spaceName}?`,
                                    body: `Its members stop reaching your data at once. Your contract with ${spaceName} stays, empty: add a realm to it again any time.`,
                                    label: 'Share nothing',
                                },
                                run: async () => { await api.revokeGroupShare(groupId); toast.success(`You share nothing with ${spaceName} now`); await load(); },
                            },
                        ].filter(Boolean)}
                    >
                        <Facts items={[
                            { label: 'From your graph', value: sharesWords(share) },
                            mine.since && { label: 'Since', value: dateOf(mine.since) },
                            sharing && { label: 'Expires', value: mine.expires ? dateOf(mine.expires) : 'Never' },
                        ]} />
                        {through.length > 0 && (
                            <Tags items={through.map((r) => ({ key: r.id, label: r.name, onPress: () => { switchToPersonal?.(); navigate(`/realms?open=${r.id}`); } }))} />
                        )}
                        {through.filter((r) => r.description).map((r) => <Note key={r.id}>{r.name}: {r.description}</Note>)}
                    </FormBand>
                    <FormBand
                        title={`What you can do in ${spaceName}`}
                        aside={access ? link('Contract', () => navigate(accessContractPath(groupId, access.epId))) : null}
                        lead={admin
                            ? `Given to you by ${spaceName}. As an admin you also run the group; the realm is what you can do with its data.`
                            : `Given to you by ${spaceName}. An admin can change it.`}
                        acts={access?.realmId ? [{ id: 'realm', label: `Open ${access.realm}`, run: () => navigate(to('realms', null, `open=${access.realmId}`)) }] : undefined}
                    >
                        <Facts items={[
                            { label: 'Role', value: admin ? 'Admin' : 'Member' },
                            { label: 'Realm', value: access?.realm || (admin ? 'Full Access' : 'No access') },
                            realmShort({ kind: access?.kind }) && { label: 'Allows', value: realmShort({ kind: access.kind }) },
                        ]} />
                    </FormBand>
                    {shared && (
                        <FormBand
                            title={`Shared into ${spaceName} by its members`}
                            aside={shared.total > shared.nodes.length ? `Showing ${shared.nodes.length} of ${shared.total}` : shared.total}
                            lead="What members have chosen to share with the group from their own data."
                        >
                            {shared.error
                                ? <Note tone="warn">Couldn’t read what is shared.</Note>
                                : <DataTable id="self-share-nodes" label="Shared by members" columns={SHARED_COLUMNS} rows={shared.nodes} keyOf={(n) => n.id} empty="Nobody shares anything with the group yet." />}
                        </FormBand>
                    )}
                </FormColumn>
            )}
        </NucleusPage>
    );
}
